import type { knownNextDevtoolsMcpToolMetadataRegistrySchema } from "schemas/agentTools/generated/local/next-devtools";
import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";
import type { ToolDisplayText } from "../shared";

import { getToolDisplayText, getToolInputString } from "../shared";

type KnownNextDevtoolsMcpToolName = Extract<
  keyof typeof knownNextDevtoolsMcpToolMetadataRegistrySchema.shape,
  string
>;

const NEXT_DEVTOOLS_TEXT_BY_TOOL_NAME = {
  browser_eval: getToolDisplayText({
    completed: "Checked the page in a browser",
    pending: "Checking the page in a browser",
    running: "Checking the page in a browser",
  }),
  nextjs_docs: getToolDisplayText({
    completed: "Looked up website docs",
    pending: "Looking up website docs",
    running: "Looking up website docs",
  }),
  nextjs_index: getToolDisplayText({
    completed: "Inspected the running website",
    pending: "Inspecting the running website",
    running: "Inspecting the running website",
  }),
} satisfies Record<
  Exclude<KnownNextDevtoolsMcpToolName, "nextjs_call">,
  ToolDisplayText
>;

const NEXT_DEVTOOLS_CALL_TEXT_BY_TOOL_NAME: Record<string, ToolDisplayText> = {
  get_errors: getToolDisplayText({
    completed: "Checked website errors",
    pending: "Checking website errors",
    running: "Checking website errors",
  }),
  get_logs: getToolDisplayText({
    completed: "Read website logs",
    pending: "Reading website logs",
    running: "Reading website logs",
  }),
  get_route_info: getToolDisplayText({
    completed: "Checked website pages",
    pending: "Checking website pages",
    running: "Checking website pages",
  }),
  get_server_action_by_id: getToolDisplayText({
    completed: "Inspected a website action",
    pending: "Inspecting a website action",
    running: "Inspecting a website action",
  }),
  get_server_actions: getToolDisplayText({
    completed: "Inspected website actions",
    pending: "Inspecting website actions",
    running: "Inspecting website actions",
  }),
  get_version_info: getToolDisplayText({
    completed: "Checked website setup",
    pending: "Checking website setup",
    running: "Checking website setup",
  }),
};

function getNextDevtoolsCallText({ toolPart }: { toolPart: OpencodeToolPart }) {
  const inputToolName = getToolInputString({
    properties: ["toolName", "tool_name"],
    toolPart,
  });
  if (!inputToolName) {
    return getToolDisplayText({
      completed: "Inspected the website",
      pending: "Inspecting the website",
      running: "Inspecting the website",
    });
  }

  const knownText = NEXT_DEVTOOLS_CALL_TEXT_BY_TOOL_NAME[inputToolName];
  if (knownText !== undefined) {
    return knownText;
  }

  const readableToolName = inputToolName.replaceAll("_", " ");
  return getToolDisplayText({
    completed: `Inspected the website with ${readableToolName}`,
    pending: `Inspecting the website with ${readableToolName}`,
    running: `Inspecting the website with ${readableToolName}`,
  });
}

export function getNextDevtoolsMcpServerToolDisplay({
  toolName,
  toolPart,
}: {
  toolName: KnownNextDevtoolsMcpToolName;
  toolPart: OpencodeToolPart;
}) {
  return {
    summary:
      toolName === "nextjs_call"
        ? getNextDevtoolsCallText({ toolPart })
        : NEXT_DEVTOOLS_TEXT_BY_TOOL_NAME[toolName],
    detail: null,
  };
}
